import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, MapPin, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppIcon } from "./icons";
import { Calendar } from "../ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { submitLead } from "../../lib/leads";

const CARS = ["Swift Dzire", "Ertiga", "Innova Crysta", "Fortuner", "Urbania"];
const TRIP_TYPES = ["Outstation One-way", "Outstation Round-trip", "Local (within Jodhpur)", "Airport Transfer"];

const fieldBase =
  "w-full bg-white/95 border border-jodhpur-primary/15 rounded-xl px-4 py-3.5 text-jodhpur-ink placeholder:text-jodhpur-ink/40 outline-none transition-all focus:border-jodhpur-secondary focus:ring-2 focus:ring-jodhpur-secondary/25 text-[15px]";

export const BookingForm = ({ variant = "light", formId = "hero-booking" }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    trip_type: "",
    pickup: "",
    drop: "",
    car: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.pickup.trim()) {
      toast.error("Please add your name, phone and pickup location.");
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      date: date ? format(date, "dd MMM yyyy") : "",
    };
    try {
      await submitLead(payload);
      toast.success("Opening WhatsApp with your booking details…", {
        description: "Our team will confirm your ride shortly.",
      });
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      data-testid={`${formId}-form`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full rounded-[26px] bg-jodhpur-bg p-6 sm:p-8 shadow-[0_30px_80px_-30px_rgba(20,41,89,0.55)] border border-jodhpur-primary/10"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="h-2 w-2 rounded-full bg-jodhpur-accent" />
        <span className="text-xs uppercase tracking-[0.25em] text-jodhpur-secondary font-medium">
          Instant Booking
        </span>
      </div>
      <h3 className="text-2xl sm:text-3xl font-semibold text-jodhpur-primary mb-5">
        Book your ride
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div className="relative">
          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-jodhpur-primary/40" />
          <input
            data-testid={`${formId}-name`}
            aria-label="Your name"
            className={`${fieldBase} pl-10`}
            placeholder="Your name"
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-jodhpur-primary/40" />
          <input
            data-testid={`${formId}-phone`}
            aria-label="Phone number"
            type="tel"
            className={`${fieldBase} pl-10`}
            placeholder="Phone number"
            value={form.phone}
            onChange={set("phone")}
          />
        </div>

        <div className="sm:col-span-2">
          <Select value={form.trip_type} onValueChange={(v) => setForm((f) => ({ ...f, trip_type: v }))}>
            <SelectTrigger data-testid={`${formId}-trip-type`} aria-label="Trip type" className={`${fieldBase} h-auto`}>
              <SelectValue placeholder="Select trip type" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {TRIP_TYPES.map((t) => (
                <SelectItem key={t} value={t} data-testid={`trip-${t}`}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-jodhpur-accent" />
          <input
            data-testid={`${formId}-pickup`}
            aria-label="Pickup location"
            className={`${fieldBase} pl-10`}
            placeholder="Pickup location"
            value={form.pickup}
            onChange={set("pickup")}
          />
        </div>
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-jodhpur-secondary" />
          <input
            data-testid={`${formId}-drop`}
            aria-label="Drop location"
            className={`${fieldBase} pl-10`}
            placeholder="Drop location"
            value={form.drop}
            onChange={set("drop")}
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`${formId}-date-trigger`}
              aria-label="Pick a travel date"
              className={`${fieldBase} flex items-center gap-2 text-left ${date ? "text-jodhpur-ink" : "text-jodhpur-ink/40"}`}
            >
              <CalendarIcon className="h-4 w-4 text-jodhpur-primary/50" />
              {date ? format(date, "dd MMM yyyy") : "Pick a date"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Select value={form.car} onValueChange={(v) => setForm((f) => ({ ...f, car: v }))}>
          <SelectTrigger data-testid={`${formId}-car`} aria-label="Choose a car" className={`${fieldBase} h-auto`}>
            <SelectValue placeholder="Choose a car" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {CARS.map((c) => (
              <SelectItem key={c} value={c} data-testid={`car-${c}`}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <button
        type="submit"
        data-testid={`${formId}-submit`}
        disabled={loading}
        className="group mt-5 w-full flex items-center justify-center gap-2.5 rounded-xl bg-[#128C7E] hover:bg-[#0f7568] text-white font-semibold py-4 text-[15px] transition-all duration-300 hover:shadow-[0_16px_40px_-12px_rgba(18,140,126,0.7)] active:scale-[0.99] disabled:opacity-70"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <WhatsAppIcon className="h-5 w-5 transition-transform group-hover:scale-110" />
        )}
        Book on WhatsApp
      </button>
      <p className="mt-3 text-center text-xs text-jodhpur-ink/50">
        Free cancellation • 24×7 support • Best fares in Jodhpur
      </p>
    </motion.form>
  );
};
